import { useEffect, useRef, useState } from 'react';
import useGsapInView from '../../hooks/useGsapInView';
import useGsapSection from '../../hooks/useGsapSection';
import { ScrambleReveal } from '../TextEffects';
import CaseStudyProcess from './CaseStudyProcess';

const DOCUMENT_CAPTION = '— Governance dashboard · Data obscured under NDA';
const VIDEO_OPEN_LABEL = 'Play clip ↗';

const DocumentFrame = ({
  src,
  alt = '',
  caption = DOCUMENT_CAPTION,
}) => (
  <figure className="cs-document-frame" data-testid="case-study-document">
    <div className="cs-document-frame-inner">
      <img src={src} alt={alt} loading="lazy" decoding="async" />
    </div>
    <figcaption className="cs-document-frame-caption">{caption}</figcaption>
  </figure>
);

const ShotCaption = ({ text, muted = false }) => {
  const ref = useRef(null);
  const inView = useGsapInView(ref);

  return (
    <p ref={ref} className={`cs-shot-caption${muted ? ' is-muted' : ''}`}>
      <ScrambleReveal text={text} externalInView={inView} delay={0.02} />
    </p>
  );
};

const MediaFrame = ({
  src,
  alt,
  variant = 'default',
  caption,
  portrait,
  placeholder = false,
  placeholderLabel = 'SCREENSHOT',
  fit,
}) => {
  const frameClass = `cs-media-frame${
    variant === 'bleed' ? ' is-bleed' : ''
  }${variant === 'centered' ? ' is-centered' : ''}${
    portrait ? ' is-portrait' : ''
  }${placeholder ? ' has-placeholder' : ''}${
    fit === 'contain' ? ' is-contain' : ''
  }`;

  const media = placeholder ? (
    <div className="cs-shot-placeholder" aria-hidden="true">
      <span className="cs-shot-placeholder-label">{placeholderLabel}</span>
    </div>
  ) : (
    <img src={src} alt={alt} loading="lazy" decoding="async" />
  );

  if (variant === 'centered') {
    return (
      <figure className={frameClass}>
        <div className="cs-media-frame-stack">
          {media}
          {caption && <ShotCaption text={caption} muted />}
        </div>
      </figure>
    );
  }

  return (
    <figure className={frameClass}>
      {media}
      {caption && <ShotCaption text={caption} />}
    </figure>
  );
};

const VideoPlaceholder = ({ poster, label = 'VIDEO' }) => (
  <div className="cs-video-placeholder" aria-hidden="true">
    {poster && (
      <img
        className="cs-video-placeholder-bg"
        src={poster}
        alt=""
        loading="lazy"
        decoding="async"
      />
    )}
    <span className="cs-video-placeholder-label">{label}</span>
  </div>
);

const VideoPlaceholderBlock = ({ poster, caption, label }) => (
  <figure className="cs-video-frame" data-testid="case-study-video-placeholder">
    <VideoPlaceholder poster={poster} label={label} />
    {caption && <ShotCaption text={caption} muted />}
  </figure>
);

const VideoBlockPlayer = ({ src, poster, caption }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      <figure className="cs-video-frame" data-testid="case-study-video">
        <button
          type="button"
          className="cs-video-btn"
          onClick={() => setOpen(true)}
          aria-label="Play video clip"
        >
          <video
            className="cs-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={poster}
          >
            <source src={src} type="video/mp4" />
          </video>
          <span className="cs-video-open" aria-hidden="true">
            {VIDEO_OPEN_LABEL}
          </span>
        </button>
        {caption && <ShotCaption text={caption} muted />}
      </figure>

      {open && (
        <div
          className="cs-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          tabIndex={-1}
        >
          <div className="cs-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="cs-lightbox-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              Close
            </button>
            <video
              className="cs-lightbox-video"
              autoPlay
              controls
              muted
              playsInline
              preload="auto"
            >
              <source src={src} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
    </>
  );
};

const VideoBlock = ({ src, poster, caption, placeholder, label }) => {
  if (placeholder) {
    return <VideoPlaceholderBlock poster={poster} caption={caption} label={label} />;
  }
  return <VideoBlockPlayer src={src} poster={poster} caption={caption} />;
};

const NoteBlock = ({ text }) => {
  if (!text) return null;
  return (
    <section className="cs-note" data-testid="case-study-note">
      <p className="cs-note-text">{text}</p>
    </section>
  );
};

const VideoTilePlayer = ({ src, poster, caption, className = '' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      <div className={`cs-video-tile ${className}`}>
        <button
          type="button"
          className="cs-video-btn"
          onClick={() => setOpen(true)}
          aria-label="Play video clip"
        >
          <video
            className="cs-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={poster}
          >
            <source src={src} type="video/mp4" />
          </video>
          <span className="cs-video-open" aria-hidden="true">
            {VIDEO_OPEN_LABEL}
          </span>
        </button>
        {caption && <ShotCaption text={caption} muted />}
      </div>

      {open && (
        <div
          className="cs-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          tabIndex={-1}
        >
          <div className="cs-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="cs-lightbox-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              Close
            </button>
            <video
              className="cs-lightbox-video"
              autoPlay
              controls
              muted
              playsInline
              preload="auto"
            >
              <source src={src} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
    </>
  );
};

const VideoTile = ({ src, poster, caption, className = '', placeholder, label }) => {
  if (placeholder) {
    return (
      <div className={`cs-video-tile ${className}`}>
        <VideoPlaceholder poster={poster} label={label} />
        {caption && <ShotCaption text={caption} muted />}
      </div>
    );
  }
  return (
    <VideoTilePlayer src={src} poster={poster} caption={caption} className={className} />
  );
};

const VideoPairBlock = ({ left, right }) => (
  <section className="cs-video-pair" data-testid="case-study-video-pair">
    <VideoTile
      src={left.src}
      poster={left.poster}
      caption={left.caption}
      placeholder={left.placeholder}
      label={left.label}
    />
    <VideoTile
      src={right.src}
      poster={right.poster}
      caption={right.caption}
      placeholder={right.placeholder}
      label={right.label}
    />
  </section>
);

const GridCell = ({ cell }) => {
  if (cell?.placeholder) {
    return <div className="cs-grid-placeholder" aria-hidden="true" />;
  }
  return (
    <img src={cell.src} alt={cell.alt || ''} loading="lazy" decoding="async" />
  );
};

const GridItem = ({ cell, silent }) => {
  if (!cell) return null;

  return (
    <div className={`cs-grid-item${cell.compact ? ' is-compact' : ''}`}>
      <div className="cs-grid-img-wrap">
        <GridCell cell={cell} />
      </div>
      {!silent && cell.caption && <ShotCaption text={cell.caption} muted />}
    </div>
  );
};

const GridBlock = ({ rows, silent = false }) => (
  <div className={`cs-grid${silent ? ' is-silent' : ''}`} data-testid="case-study-grid">
    {rows.map((row, i) => {
      const hasCellCaptions = Boolean(row.left?.caption || row.right?.caption);

      return (
        <figure key={`grid-row-${i}`} className="cs-grid-row">
          <div className="cs-grid-pair">
            <GridItem cell={row.left} silent={silent} />
            <GridItem cell={row.right} silent={silent} />
          </div>
          {!silent && !hasCellCaptions && row.caption && (
            <ShotCaption text={row.caption} muted />
          )}
        </figure>
      );
    })}
  </div>
);

const SplitBlock = ({
  src,
  alt,
  caption,
  title,
  paragraphs = [],
  portrait = false,
  reverse = false,
}) => {
  const ref = useRef(null);
  const inView = useGsapSection(ref);
  const copy = paragraphs.filter(Boolean);

  if (!src || copy.length === 0) return null;

  return (
    <section
      ref={ref}
      className={`cs-split${reverse ? ' is-reverse' : ''}`}
      data-testid="case-study-split"
    >
      <figure className={`cs-split-media gsap-reveal${portrait ? ' is-portrait' : ''}`}>
        <img src={src} alt={alt || ''} loading="lazy" decoding="async" />
        {caption && (
          <figcaption className="cs-split-caption">
            <ScrambleReveal text={caption} externalInView={inView} delay={0.04} />
          </figcaption>
        )}
      </figure>
      <div className="cs-split-content gsap-reveal">
        {title && (
          <span className="cs-split-label">
            <ScrambleReveal text={title} externalInView={inView} delay={0.02} />
          </span>
        )}
        <div className="cs-split-copy">
          {copy.map((paragraph, i) => (
            <p key={i} className="cs-split-paragraph">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

const PairBlock = ({ left, right, portrait = false }) => {
  const ref = useRef(null);

  return (
    <figure
      className={`cs-pair cs-pair--equal${portrait ? ' is-portrait' : ''}`}
      ref={ref}
      data-testid="case-study-pair"
    >
      <div className="cs-pair-grid">
        <div className="cs-pair-item">
          <div className="cs-pair-img-wrap">
            <img src={left.src} alt={left.alt} loading="lazy" decoding="async" />
          </div>
          {left.caption && (
            <ShotCaption text={left.caption} />
          )}
        </div>
        <div className="cs-pair-item">
          <div className="cs-pair-img-wrap">
            <img src={right.src} alt={right.alt} loading="lazy" decoding="async" />
          </div>
          {right.caption && (
            <ShotCaption text={right.caption} />
          )}
        </div>
      </div>
    </figure>
  );
};

const CaseStudyBody = ({ blocks }) => {
  if (!blocks?.length) return null;

  return (
    <div className="cs-body" data-testid="case-study-body">
      {blocks.map((block, i) => {
        if (block.type === 'full' || block.type === 'shot') {
          return (
            <MediaFrame
              key={`${block.src || 'ph'}-${i}`}
              src={block.src}
              alt={block.alt}
              variant={block.variant || 'default'}
              caption={block.caption}
              portrait={block.portrait}
              placeholder={block.placeholder}
              placeholderLabel={block.placeholderLabel}
              fit={block.fit}
            />
          );
        }

        if (block.type === 'video') {
          return (
            <VideoBlock
              key={`video-${i}`}
              src={block.src}
              poster={block.poster}
              caption={block.caption}
              placeholder={block.placeholder}
              label={block.label}
            />
          );
        }

        if (block.type === 'videoPair') {
          return (
            <VideoPairBlock
              key={`video-pair-${i}`}
              left={block.left}
              right={block.right}
            />
          );
        }

        if (block.type === 'note') {
          return <NoteBlock key={`note-${i}`} text={block.text} />;
        }

        if (block.type === 'pair') {
          return (
            <PairBlock
              key={`pair-${i}`}
              left={block.left}
              right={block.right}
              portrait={block.portrait}
            />
          );
        }

        if (block.type === 'grid') {
          return (
            <GridBlock key={`grid-${i}`} rows={block.rows} silent={block.silent} />
          );
        }

        if (block.type === 'document') {
          return (
            <DocumentFrame
              key={`doc-${block.src}-${i}`}
              src={block.src}
              alt={block.alt}
              caption={block.caption}
            />
          );
        }

        if (block.type === 'process') {
          return (
            <CaseStudyProcess
              key={`process-${i}`}
              title={block.title}
              paragraphs={block.paragraphs}
              layout={block.layout || 'narrow'}
            />
          );
        }

        if (block.type === 'split') {
          return (
            <SplitBlock
              key={`split-${block.src}-${i}`}
              src={block.src}
              alt={block.alt}
              caption={block.caption}
              title={block.title}
              paragraphs={block.paragraphs}
              portrait={block.portrait}
              reverse={block.reverse}
            />
          );
        }

        return null;
      })}
    </div>
  );
};

export default CaseStudyBody;
